import { ActionIcon, Breadcrumbs, Center, SegmentedControl, Stepper, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle, IconRefresh } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import type {
    CreateContainerRequest,
    CreateContainerSourceRequest,
    EnvironmentVariable,
    PricingType,
} from '@xver/apis/v1/kufer/containers/model';
import { Button } from '@xver/components/Atoms';
import ActivationGuard from '@xver/components/Atoms/ActivationGuard';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import InputField from '@xver/components/Atoms/InputField/InputField';
import { DeployProjectSection, DeployTargetNotice } from '@xver/components/Molecules';
import { useActiveProject } from '@xver/hooks/useActiveProject';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import { getErrorMessage } from '@xver/utils/error';
import ContainerConfigForm, {
    type ContainerConfigFormValue,
    DEFAULT_CONTAINER_CONFIG_VALUE,
    DEFAULT_ENTRYPOINT_VALUE,
    type EntrypointValue,
} from './components/ContainerConfigForm';
import ContainerDeploymentSummaryCard from './components/ContainerDeploymentSummaryCard';
import EnvironmentVariablesEditor from './components/EnvironmentVariablesEditor';
import GitHubSourceForm, {
    DEFAULT_GITHUB_SOURCE_VALUE,
    type GitHubSourceFormValue,
    toCreateSourceRequest as githubToRequest,
} from './components/GitHubSourceForm';
import InlineSourceForm, {
    DEFAULT_INLINE_SOURCE_VALUE,
    type InlineSourceFormValue,
    toCreateSourceRequest as inlineToRequest,
} from './components/InlineSourceForm';
import ReviewStep from './components/ReviewStep';
import SourceTypeSelector, { type SourceType } from './components/SourceTypeSelector';
import UrlSourceForm, {
    DEFAULT_URL_SOURCE_VALUE,
    type UrlSourceFormValue,
    toCreateSourceRequest as urlToRequest,
} from './components/UrlSourceForm';
import { useCreateContainerMutation } from './hooks/useCreateContainerMutation';
import { generateServiceName } from './utils/naming';
import {
    type DeploymentField,
    type DeploymentFormState,
    DEPLOYMENT_STEPS,
    ERROR_DEFAULTS,
    type ErrorKey,
    isStepValid,
    STEP_CONFIGURATION,
    STEP_REVIEW,
    STEP_SOURCE,
    type StepErrors,
    validateStep,
} from './utils/validation';

const CONTAINER_LIST_QUERY_KEY = ['apis.v1.kufer.containers.list'];

/** Stable identity so `visibleErrors` doesn't churn on every render. */
const EMPTY_STEP_ERRORS: StepErrors = Object.freeze({});

const STEP_LABEL_DEFAULTS = ['Source', 'Configuration', 'Review & deploy'];
const STEP_DESCRIPTION_DEFAULTS = [
    'Name, pricing, and image source',
    'Port, resources, scaling, and variables',
    'Check everything, then deploy',
];

export default function ContainerEngineDeploymentPage() {
    const { t } = useTranslation('containerEngine');
    const headingRef = usePageHeading(t('deployment.page.title', 'Deploy Container'));
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const deployReasonId = useId();
    const pricingLabelId = useId();
    const { project, can } = useActiveProject();
    const canDeploy = can('container', 'manage');
    const canUseOwnerSources = !project || project.role === 'owner';

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product', 'Products'), href: '#' },
        {
            title: t('breadcrumb.containerEngine', 'Container Engine'),
            href: '/container-engine',
        },
        {
            title: t('breadcrumb.deployment', 'Deploy Container'),
            href: '/container-engine/deployment',
        },
    ]);

    const [name, setName] = useState(() => generateServiceName());
    const [pricingType, setPricingType] = useState<PricingType>('request_based');

    const [sourceType, setSourceType] = useState<SourceType>('url');
    const [urlSource, setUrlSource] = useState<UrlSourceFormValue>(DEFAULT_URL_SOURCE_VALUE);
    const [inlineSource, setInlineSource] = useState<InlineSourceFormValue>(
        DEFAULT_INLINE_SOURCE_VALUE
    );
    const [githubSource, setGithubSource] = useState<GitHubSourceFormValue>(
        DEFAULT_GITHUB_SOURCE_VALUE
    );

    const effectiveUrlSource = useMemo(
        () => (canUseOwnerSources ? urlSource : { ...urlSource, registry_credential_id: '' }),
        [canUseOwnerSources, urlSource]
    );

    const [containerConfig, setContainerConfig] = useState<ContainerConfigFormValue>(
        DEFAULT_CONTAINER_CONFIG_VALUE
    );
    const [entrypoint, setEntrypoint] = useState<EntrypointValue>(DEFAULT_ENTRYPOINT_VALUE);
    const [environmentVariables, setEnvironmentVariables] = useState<EnvironmentVariable[]>([]);

    const [active, setActive] = useState(0);
    // Steps become clickable only once reached, so the stepper can't be used to skip validation.
    const [maxVisited, setMaxVisited] = useState(0);
    // A step's errors stay hidden until the user tries to leave it.
    const [attempted, setAttempted] = useState<boolean[]>([false, false, false]);
    const [focusIntent, setFocusIntent] = useState<'heading' | 'error' | null>(null);

    const stepHeadingRef = useRef<HTMLHeadingElement>(null);
    const stepContentRef = useRef<HTMLDivElement>(null);

    const formState: DeploymentFormState = useMemo(
        () => ({
            name,
            sourceType,
            urlSource: effectiveUrlSource,
            inlineSource,
            githubSource,
            canUseOwnerSources,
            config: containerConfig,
        }),
        [
            name,
            sourceType,
            effectiveUrlSource,
            inlineSource,
            githubSource,
            canUseOwnerSources,
            containerConfig,
        ]
    );

    const stepErrors = useMemo(() => validateStep(active, formState), [active, formState]);
    const visibleErrors = attempted[active] ? stepErrors : EMPTY_STEP_ERRORS;

    const allValid = useMemo(
        () => isStepValid(STEP_SOURCE, formState) && isStepValid(STEP_CONFIGURATION, formState),
        [formState]
    );

    const message = (key?: ErrorKey) =>
        key ? t(`deployment.errors.${key}`, ERROR_DEFAULTS[key]) : undefined;

    const revealStep = (step: number) => {
        setAttempted((prev) => prev.map((value, index) => (index === step ? true : value)));
        setActive(step);
        setFocusIntent('error');
    };

    const goToStep = (next: number) => {
        if (next === active) return;

        if (next > active) {
            // Every step we would skip over has to be clean first.
            for (let step = active; step < next; step++) {
                if (!isStepValid(step, formState)) {
                    revealStep(step);
                    return;
                }
            }
        }

        setMaxVisited((prev) => Math.max(prev, next));
        setActive(next);
        setFocusIntent('heading');
    };

    const handleNext = () => goToStep(Math.min(active + 1, DEPLOYMENT_STEPS.length - 1));

    const handleBack = () => {
        if (active === 0) return;
        setActive(active - 1);
        setFocusIntent('heading');
    };

    useEffect(() => {
        if (!focusIntent) return;

        if (focusIntent === 'error') {
            // Mantine inputs and InputField both set aria-invalid from their error prop, so the
            // first match is the first bad control in DOM order.
            const firstInvalid =
                stepContentRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
            (firstInvalid ?? stepHeadingRef.current)?.focus();
        } else {
            stepHeadingRef.current?.focus();
        }

        setFocusIntent(null);
    }, [focusIntent, active]);

    const buildSource = (): CreateContainerSourceRequest => {
        if (sourceType === 'url') return urlToRequest(effectiveUrlSource);
        if (sourceType === 'inline') return inlineToRequest(inlineSource);
        return githubToRequest(githubSource);
    };

    const createMutation = useCreateContainerMutation({
        onSuccess: async (data) => {
            notifications.show({
                title: t('deployment.toast.successTitle', 'Container deployment started'),
                message: t(
                    'deployment.toast.successMessage',
                    'Build pipeline is running. We will redirect you to the container details.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY });
            navigate(`/container-engine/${data.id}`);
        },
        onError: (error) => {
            notifications.show({
                title: t('deployment.toast.errorTitle', 'Deployment failed'),
                message: getErrorMessage(error),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleSubmit = () => {
        if (!allValid) {
            revealStep(isStepValid(STEP_SOURCE, formState) ? STEP_CONFIGURATION : STEP_SOURCE);
            return;
        }

        const filteredEnv = environmentVariables.filter((e) => e.name.trim().length > 0);

        const payload: CreateContainerRequest = {
            name: name.trim(),
            source: buildSource(),
            pricing_type: pricingType,
            container_port: containerConfig.container_port,
            cpu: containerConfig.cpu,
            memory: containerConfig.memory,
            request_timeout: containerConfig.request_timeout,
            request_concurrency: containerConfig.request_concurrency,
            min_replicas: containerConfig.min_replicas,
            max_replicas: containerConfig.max_replicas,
            entrypoint_command:
                entrypoint.entrypoint_command.length > 0
                    ? entrypoint.entrypoint_command
                    : undefined,
            entrypoint_argument:
                entrypoint.entrypoint_argument.length > 0
                    ? entrypoint.entrypoint_argument
                    : undefined,
            environment_variables: filteredEnv.length > 0 ? filteredEnv : undefined,
        };

        createMutation.mutate(payload);
    };

    const errorList = Object.entries(visibleErrors) as [DeploymentField, ErrorKey][];

    const errorSummary = errorList.length > 0 && (
        <div role="alert" className="w-full p-3 rounded-md border border-red-500/40 bg-red-500/10">
            <p className="text-sm font-medium text-red-300 light:text-red-700">
                {t('deployment.stepper.errorSummaryTitle', 'Fix the following before continuing')}
            </p>
            <ul className="mt-1 pl-5 list-disc text-sm text-red-300/90 light:text-red-700">
                {errorList.map(([field, key]) => (
                    <li key={field}>{message(key)}</li>
                ))}
            </ul>
        </div>
    );

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-row justify-between">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                            {t('deployment.page.title', 'Deploy Container')}
                        </h1>
                        <p className="w-auto max-w-180 text-base text-fg/68">
                            {t(
                                'deployment.page.description',
                                'Configure a new container service. Pick a source, set runtime parameters, and deploy.'
                            )}
                        </p>
                    </div>
                </div>
            </div>

            <div className="w-full">
                <div className="w-full grid grid-cols-1 xl:grid-cols-[2.5fr_1fr] gap-4 mb-4 empty:hidden">
                    <DeployProjectSection product="container" />
                </div>
                <ActivationGuard>
                    <div className="w-full h-auto grid grid-cols-1 xl:grid-cols-[2.5fr_1fr] items-start gap-4">
                        <div className="w-full flex flex-col gap-4">
                            {/* Focusing this on step change announces the new step to screen
                                readers without needing a separate live region. */}
                            <h2
                                ref={stepHeadingRef}
                                tabIndex={-1}
                                className="text-xl font-bold outline-none"
                            >
                                {t('deployment.stepper.position', {
                                    index: active + 1,
                                    total: DEPLOYMENT_STEPS.length,
                                    defaultValue: 'Step {{index}} of {{total}}',
                                })}
                                {' · '}
                                {t(
                                    `deployment.stepper.${DEPLOYMENT_STEPS[active]}.label`,
                                    STEP_LABEL_DEFAULTS[active]
                                )}
                            </h2>

                            <Stepper
                                active={active}
                                onStepClick={goToStep}
                                size="sm"
                                radius="md"
                                role="group"
                                aria-label={t(
                                    'deployment.stepper.ariaLabel',
                                    'Container deployment steps'
                                )}
                            >
                                <Stepper.Step
                                    label={t(
                                        'deployment.stepper.source.label',
                                        STEP_LABEL_DEFAULTS[STEP_SOURCE]
                                    )}
                                    description={t(
                                        'deployment.stepper.source.description',
                                        STEP_DESCRIPTION_DEFAULTS[STEP_SOURCE]
                                    )}
                                    allowStepSelect={STEP_SOURCE <= maxVisited}
                                    aria-current={active === STEP_SOURCE ? 'step' : undefined}
                                >
                                    <div
                                        ref={active === STEP_SOURCE ? stepContentRef : undefined}
                                        className="w-full flex flex-col gap-4 pt-4"
                                    >
                                        {errorSummary}
                                        <DialogBox
                                            label={t(
                                                'deployment.sections.basics.title',
                                                'Service basics'
                                            )}
                                            description={t(
                                                'deployment.sections.basics.description',
                                                'Give your service a name and choose how you want to pay for it.'
                                            )}
                                            classNames="p-4 gap-4"
                                        >
                                            <InputField
                                                size="md"
                                                label={
                                                    <h3 className="text-base">
                                                        {t(
                                                            'deployment.fields.name',
                                                            'Service name'
                                                        )}
                                                    </h3>
                                                }
                                                placeholder="my-service"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                error={message(visibleErrors.name)}
                                                descriptionBottom={t(
                                                    'deployment.fields.nameDescription',
                                                    'Lowercase letters, digits and hyphens. 3-63 characters.'
                                                )}
                                                rightIcon={
                                                    <Tooltip
                                                        label={t(
                                                            'deployment.fields.nameRandomize',
                                                            'Generate random name'
                                                        )}
                                                        withArrow
                                                    >
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray"
                                                            size="sm"
                                                            onClick={() =>
                                                                setName(generateServiceName())
                                                            }
                                                            aria-label={t(
                                                                'deployment.fields.nameRandomize',
                                                                'Generate random name'
                                                            )}
                                                        >
                                                            <IconRefresh size={16} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                }
                                            />
                                            <div className="w-full flex flex-col gap-1">
                                                <span
                                                    id={pricingLabelId}
                                                    className="text-sm font-medium text-fg/85"
                                                >
                                                    {t(
                                                        'deployment.fields.pricing',
                                                        'Pricing model'
                                                    )}
                                                </span>
                                                <SegmentedControl
                                                    aria-labelledby={pricingLabelId}
                                                    fullWidth
                                                    size="sm"
                                                    radius="md"
                                                    value={pricingType}
                                                    onChange={(v) =>
                                                        setPricingType(v as PricingType)
                                                    }
                                                    styles={{
                                                        root: {
                                                            width: '100%',
                                                            backgroundColor: 'var(--surface-2)',
                                                            border: '0.5px solid var(--line)',
                                                        },
                                                    }}
                                                    withItemsBorders={false}
                                                    data={[
                                                        {
                                                            value: 'request_based',
                                                            label: (
                                                                <Center style={{ gap: 6 }}>
                                                                    <span
                                                                        style={{ fontWeight: 400 }}
                                                                    >
                                                                        {t(
                                                                            'deployment.pricing.requestBased',
                                                                            'Request-based'
                                                                        )}
                                                                    </span>
                                                                    <span className="text-xs text-fg/68">
                                                                        {t(
                                                                            'deployment.pricing.requestBasedHelper',
                                                                            'scale to zero'
                                                                        )}
                                                                    </span>
                                                                </Center>
                                                            ),
                                                        },
                                                        {
                                                            value: 'instance_based',
                                                            label: (
                                                                <Center style={{ gap: 6 }}>
                                                                    <span
                                                                        style={{ fontWeight: 400 }}
                                                                    >
                                                                        {t(
                                                                            'deployment.pricing.instanceBased',
                                                                            'Instance-based'
                                                                        )}
                                                                    </span>
                                                                    <span className="text-xs text-fg/68">
                                                                        {t(
                                                                            'deployment.pricing.instanceBasedHelper',
                                                                            'always on'
                                                                        )}
                                                                    </span>
                                                                </Center>
                                                            ),
                                                        },
                                                    ]}
                                                />
                                                <span className="text-xs text-fg/68">
                                                    {t(
                                                        'deployment.fields.pricingDescription',
                                                        'Request-based charges only for the requests your service handles; instance-based charges for the entire instance lifetime.'
                                                    )}
                                                </span>
                                            </div>
                                        </DialogBox>

                                        <DialogBox
                                            label={t('deployment.sections.source.title', 'Source')}
                                            description={t(
                                                'deployment.sections.source.description',
                                                'Where should we get the container image from?'
                                            )}
                                            classNames="p-4 gap-4"
                                        >
                                            <SourceTypeSelector
                                                value={sourceType}
                                                onChange={setSourceType}
                                            />
                                            {!canUseOwnerSources && (
                                                <p className="text-sm text-fg/68">
                                                    {t('projects:deploy.ownerOnlySources')}
                                                </p>
                                            )}
                                            {sourceType === 'url' && (
                                                <UrlSourceForm
                                                    value={effectiveUrlSource}
                                                    onChange={setUrlSource}
                                                    error={message(visibleErrors.sourceUrlImage)}
                                                    allowCredential={canUseOwnerSources}
                                                />
                                            )}
                                            {sourceType === 'inline' && (
                                                <InlineSourceForm
                                                    value={inlineSource}
                                                    onChange={setInlineSource}
                                                    error={message(
                                                        visibleErrors.sourceInlineContent
                                                    )}
                                                />
                                            )}
                                            {sourceType === 'github' && canUseOwnerSources && (
                                                <GitHubSourceForm
                                                    value={githubSource}
                                                    onChange={setGithubSource}
                                                    error={message(visibleErrors.sourceGithub)}
                                                />
                                            )}
                                        </DialogBox>
                                    </div>
                                </Stepper.Step>

                                <Stepper.Step
                                    label={t(
                                        'deployment.stepper.configuration.label',
                                        STEP_LABEL_DEFAULTS[STEP_CONFIGURATION]
                                    )}
                                    description={t(
                                        'deployment.stepper.configuration.description',
                                        STEP_DESCRIPTION_DEFAULTS[STEP_CONFIGURATION]
                                    )}
                                    allowStepSelect={STEP_CONFIGURATION <= maxVisited}
                                    aria-current={
                                        active === STEP_CONFIGURATION ? 'step' : undefined
                                    }
                                >
                                    <div
                                        ref={
                                            active === STEP_CONFIGURATION
                                                ? stepContentRef
                                                : undefined
                                        }
                                        className="w-full flex flex-col gap-4 pt-4"
                                    >
                                        {errorSummary}
                                        <DialogBox
                                            label={t(
                                                'deployment.sections.config.title',
                                                'Service configuration'
                                            )}
                                            description={t(
                                                'deployment.sections.config.description',
                                                'Networking, resource limits, and autoscaling.'
                                            )}
                                            classNames="p-4 gap-4"
                                        >
                                            <ContainerConfigForm
                                                value={containerConfig}
                                                onChange={setContainerConfig}
                                                entrypoint={entrypoint}
                                                onEntrypointChange={setEntrypoint}
                                                errors={{
                                                    container_port: message(
                                                        visibleErrors.container_port
                                                    ),
                                                    cpu: message(visibleErrors.cpu),
                                                    memory: message(visibleErrors.memory),
                                                    request_timeout: message(
                                                        visibleErrors.request_timeout
                                                    ),
                                                    request_concurrency: message(
                                                        visibleErrors.request_concurrency
                                                    ),
                                                    min_replicas: message(
                                                        visibleErrors.min_replicas
                                                    ),
                                                    max_replicas: message(
                                                        visibleErrors.max_replicas
                                                    ),
                                                }}
                                            />
                                        </DialogBox>

                                        <DialogBox
                                            label={t(
                                                'deployment.sections.env.title',
                                                'Environment variables'
                                            )}
                                            description={t(
                                                'deployment.sections.env.description',
                                                'Injected into the container at runtime.'
                                            )}
                                            classNames="p-4 gap-4"
                                        >
                                            <EnvironmentVariablesEditor
                                                value={environmentVariables}
                                                onChange={setEnvironmentVariables}
                                            />
                                        </DialogBox>
                                    </div>
                                </Stepper.Step>

                                <Stepper.Step
                                    label={t(
                                        'deployment.stepper.review.label',
                                        STEP_LABEL_DEFAULTS[STEP_REVIEW]
                                    )}
                                    description={t(
                                        'deployment.stepper.review.description',
                                        STEP_DESCRIPTION_DEFAULTS[STEP_REVIEW]
                                    )}
                                    allowStepSelect={STEP_REVIEW <= maxVisited}
                                    aria-current={active === STEP_REVIEW ? 'step' : undefined}
                                >
                                    <div
                                        ref={active === STEP_REVIEW ? stepContentRef : undefined}
                                        className="w-full flex flex-col gap-4 pt-4"
                                    >
                                        {!allValid && (
                                            <div
                                                role="alert"
                                                className="w-full flex flex-col items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10"
                                            >
                                                <p className="text-sm font-medium text-red-300 light:text-red-700">
                                                    {t(
                                                        'deployment.stepper.reviewBlockedTitle',
                                                        'Some steps still need attention'
                                                    )}
                                                </p>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() =>
                                                        revealStep(
                                                            isStepValid(STEP_SOURCE, formState)
                                                                ? STEP_CONFIGURATION
                                                                : STEP_SOURCE
                                                        )
                                                    }
                                                >
                                                    {t('deployment.stepper.goToStep', 'Go to step')}
                                                </Button>
                                            </div>
                                        )}
                                        <ReviewStep
                                            sourceType={sourceType}
                                            urlSource={effectiveUrlSource}
                                            inlineSource={inlineSource}
                                            githubSource={githubSource}
                                            config={containerConfig}
                                            entrypoint={entrypoint}
                                            environmentVariables={environmentVariables}
                                            onEditStep={goToStep}
                                        />
                                    </div>
                                </Stepper.Step>
                            </Stepper>

                            <div className="w-full flex flex-row justify-between items-center gap-3 pt-2">
                                <Button
                                    variant="secondary"
                                    onClick={handleBack}
                                    disabled={active === 0}
                                >
                                    {t('deployment.stepper.back', 'Back')}
                                </Button>
                                {active < DEPLOYMENT_STEPS.length - 1 && (
                                    <Button onClick={handleNext}>
                                        {t('deployment.stepper.next', 'Next')}
                                    </Button>
                                )}
                            </div>
                        </div>

                        <ContainerDeploymentSummaryCard
                            name={name}
                            pricingType={pricingType}
                            sourceType={sourceType}
                            config={containerConfig}
                            onDeploy={handleSubmit}
                            isDeploying={createMutation.isPending}
                            isValid={allValid}
                            showDeployAction={active === STEP_REVIEW}
                            notice={
                                <DeployTargetNotice product="container" reasonId={deployReasonId} />
                            }
                            deployBlockedReasonId={canDeploy ? undefined : deployReasonId}
                        />
                    </div>
                </ActivationGuard>
            </div>
        </div>
    );
}
